import { useAppTheme } from '@/providers/theme-provider';
import { useEffect, useState } from 'react';
import { Dimensions, Pressable, View } from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Sidebar } from './sidebar';

const DRAWER_WIDTH = 280;
const ANIMATION_DURATION = 250;

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  unreadCount?: number;
  onCheckIn: () => void;
}

export function MobileDrawer({
  isOpen,
  onClose,
  unreadCount,
  onCheckIn,
}: MobileDrawerProps) {
  const theme = useAppTheme();
  const [isVisible, setIsVisible] = useState(false);

  const translateX = useSharedValue(-DRAWER_WIDTH);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      translateX.value = withTiming(0, { duration: ANIMATION_DURATION });
      opacity.value = withTiming(0.5, { duration: ANIMATION_DURATION });
    } else {
      translateX.value = withTiming(-DRAWER_WIDTH, { duration: ANIMATION_DURATION }, (finished) => {
        if (finished) {
          runOnJS(setIsVisible)(false);
        }
      });
      opacity.value = withTiming(0, { duration: ANIMATION_DURATION });
    }
  }, [isOpen, translateX, opacity]);

  const drawerStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const handleCheckIn = () => {
    onClose();
    onCheckIn();
  };

  const { height } = Dimensions.get('window');

  if (!isVisible) {
    return null;
  }

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
      }}
      pointerEvents={isOpen ? 'auto' : 'none'}
    >
      <Pressable
        onPress={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        pointerEvents={isOpen ? 'auto' : 'none'}
      >
        <Animated.View
          style={[
            {
              flex: 1,
              backgroundColor: 'black',
            },
            overlayStyle,
          ]}
        />
      </Pressable>

      <Animated.View
        style={[
          {
            position: 'absolute',
            top: 0,
            left: 0,
            width: DRAWER_WIDTH,
            height: height,
            backgroundColor: theme.colors.tabBarBackground,
            borderRightWidth: 1,
            borderRightColor: theme.colors.border,
            paddingTop: 48,
            paddingBottom: 24,
          },
          drawerStyle,
        ]}
      >
        <Sidebar
          unreadCount={unreadCount}
          onCheckIn={handleCheckIn}
          onClose={onClose}
          onNavigate={onClose}
        />
      </Animated.View>
    </View>
  );
}
